import * as React from "react";
import { LoginResponse, User } from "../components/auth/auth.types";
import { jwtDecode } from "jwt-decode";

export interface AuthContext {
	isAuthenticated: boolean;
	login: (loginResponse: LoginResponse) => void;
	logout: () => void;
	user: User | null;
	token: string | null;
	isExpired: () => boolean;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AuthContext = React.createContext<AuthContext | null>(null);

const key = "auth.user";

function getStoredUser() {
	return localStorage.getItem(key);
}

function setStoredUser(auth: LoginResponse | null) {
	if (auth) {
		const stringifyAuth = JSON.stringify(auth);
		localStorage.setItem(key, stringifyAuth);
		window.dispatchEvent(
			new StorageEvent("storage", { key, newValue: stringifyAuth }),
		);
	} else {
		localStorage.removeItem(key);
		window.dispatchEvent(new StorageEvent("storage", { key }));
	}
}

const subscribe = (listener: () => void) => {
	window.addEventListener("storage", listener);
	return () => window.removeEventListener("storage", listener);
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const auth = React.useSyncExternalStore(subscribe, getStoredUser);
	const [open, setOpen] = React.useState(false);
	const parsedAuth = auth ? (JSON.parse(auth) as LoginResponse) : null;
	const isAuthenticated = !!parsedAuth?.user;

	const isExpired = () => {
		const raw = localStorage.getItem(key);
		const freshAuth = raw ? (JSON.parse(raw) as LoginResponse) : null;
		const freshExp = freshAuth ? jwtDecode(freshAuth.accessToken)?.exp : null;
		if (!freshExp) {
			setOpen(false);
			return false;
		}
		const result = Date.now() >= freshExp * 1000;
		setOpen(result);
		return result;
	};

	const logout = React.useCallback(() => {
		setStoredUser(null);
	}, []);

	const login = React.useCallback((loginResponse: LoginResponse) => {
		setStoredUser(loginResponse);
	}, []);

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated,
				isExpired,
				user: parsedAuth?.user || null,
				login,
				logout,
				token: parsedAuth?.accessToken || null,
				open,
				setOpen,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}
